'use client';

import { useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Inbox, Pencil, Printer, SlidersHorizontal, Upload } from 'lucide-react';
import { RoleGuard } from '@/components/rbac/RoleGuard';
import { AUTHENTICATED_ROLES } from '@/components/layout/navigation';
import { useInvestigatorProfilePermissions } from '@/hooks/useInvestigatorProfilePermissions';
import { useTopbarPortalTarget } from '@/hooks/useTopbarPortalTarget';
import { useIsMounted } from '@/hooks/useIsMounted';
import { ShareProfileButton } from './ShareProfileButton';
import { BackToDirectoryButton } from './BackToDirectoryButton';
import { OverflowMenu, type OverflowMenuItem } from '@/components/ui/OverflowMenu';
import { PreferenciasHubModal } from '@/components/investigador/preferencias/PreferenciasHubModal';
import { MisSolicitudesModal } from './MisSolicitudesModal';
import sidebarStyles from '@/components/investigador/sidebar/sidebar.module.css';

/** Secciones del dueño que abren en modal desde la barra de acciones. */
type Seccion = 'preferencias' | 'solicitudes';

function parseSeccion(valor: string | null): Seccion | null {
	return valor === 'preferencias' || valor === 'solicitudes' ? valor : null;
}

interface ProfileActionsProps {
	targetProfile: {
		slug: string;
		email?: string | null;
		idInvestigador?: number | null;
		repoUrl: string;
		/** Nombre visible: titula las vistas que un administrador abre sobre este perfil. */
		displayName?: string | null;
	};
	targetUnit?: {
		id?: string | null;
	};
	targetMacroUnit?: {
		id?: string | null;
	};
}

export function ProfileActions({
	targetProfile,
	targetUnit,
	targetMacroUnit,
}: ProfileActionsProps) {
	const pathname = usePathname();
	const permissions = useInvestigatorProfilePermissions({
		targetProfile,
		targetUnit,
		targetMacroUnit,
	});

	const { action, user } = permissions;

	const portalTarget = useTopbarPortalTarget();
	const montado = useIsMounted();

	// Las secciones del dueño abren en modal y no en ruta propia: `/investigadores/
	// [slug]/*` es espacio PÚBLICO, así que una ruta ahí arrastra el trabajo de
	// noindex, robots y gateo que ya costó en /exportar y /editar. El modal no
	// necesita nada de eso — y para poder enlazarlo igual (el menú de usuario
	// apunta directo a Preferencias), también se abre con `?seccion=…`.
	const router = useRouter();
	const searchParams = useSearchParams();
	const seccionEnUrl = parseSeccion(searchParams.get('seccion'));
	const [seccionLocal, setSeccionLocal] = useState<Seccion | null>(null);

	// Se DERIVA de la URL en vez de copiarse a estado en un efecto: copiarlo choca
	// con `react-hooks/set-state-in-effect` y, sobre todo, deja dos fuentes para el
	// mismo hecho. Abrir desde un botón no toca la URL; cerrar sí la limpia si el
	// modal había llegado por enlace.
	const seccion = seccionLocal ?? seccionEnUrl;

	function cerrarSeccion() {
		setSeccionLocal(null);
		if (seccionEnUrl) router.replace(pathname, { scroll: false });
	}

	// Todo cuelga del slug público: `/mi-perfil/*` solo sobrevive como redirect.
	// Antes esto alternaba entre las dos jerarquías según la ruta de origen, y el
	// botón "atrás" del header terminaba en una rama distinta de la que veníamos.
	const actionHref = `/investigadores/${targetProfile.slug}/editar`;
	const exportHref = `/investigadores/${targetProfile.slug}/exportar`;

	const actionLabel = 'Editar perfil';

	// Ownership by idInvestigador only — email fallback removed (G3).
	const isOwnProfile = Boolean(
		user?.idInvestigador != null && targetProfile.idInvestigador != null && user.idInvestigador === targetProfile.idInvestigador,
	);
	// Cualquier usuario logueado puede exportar: no dueño/no admin obtiene solo lo
	// público; dueño/admin obtiene el CV completo (lo resuelve la página + el backend).
	// El RoleGuard de abajo exige sesión (el perfil en sí es público: ver `proxy.ts`).
	const canExport = Boolean(user);

	// Un administrador parado en el perfil de OTRA persona (R63). Ve las mismas dos
	// secciones, en modo consulta: el historial de solicitudes y las preferencias.
	// Puede LEER en qué quedó cada cosa, no responder por nadie — el consentimiento
	// solo se reinicia, y eso vive en `/admin/configuraciones`.
	const vistaAjenaAdmin = Boolean(user?.role === 'admin') && !isOwnProfile;
	const verSecciones = isOwnProfile || vistaAjenaAdmin;

	// Compartir NO va tras el RoleGuard: la url es pública y el perfil es visible
	// sin sesión, así que un visitante anónimo también puede copiar el enlace.
	const shareButton = <ShareProfileButton slug={targetProfile.slug} isOwnProfile={isOwnProfile} />;

	const editButton = action !== 'none' ? (
		<RoleGuard allowedRoles={AUTHENTICATED_ROLES}>
			<Link href={actionHref} className={sidebarStyles.btnPrimary}>
				<Pencil size={13} />
				{actionLabel}
			</Link>
		</RoleGuard>
	) : null;

	const exportButton = canExport ? (
		<RoleGuard allowedRoles={AUTHENTICATED_ROLES}>
			<Link href={exportHref} className={sidebarStyles.btnSecondary}>
				<Printer size={13} />
				Exportar CV
			</Link>
		</RoleGuard>
	) : null;

	// R35 · placeholder for the future ORCID upload flow (R39). Owner-only because
	// pushing data to ORCID is a self-action that requires the person's consent.
	// Disabled with a "Soon" superscript badge (top-right, not inline) — same visual
	// language as the disabled "Proyectos" tab in the profile.
	const orcidButton = isOwnProfile ? (
		<RoleGuard allowedRoles={AUTHENTICATED_ROLES}>
			<span style={{ position: 'relative', display: 'inline-flex' }}>
				<button
					type="button"
					disabled
					className={sidebarStyles.btnSecondary}
					title="Próximamente: enviar tus datos a ORCID"
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: '0.4rem',
						opacity: 0.6,
						cursor: 'not-allowed',
					}}
				>
					<Upload size={13} />
					Subir a ORCID
				</button>
				<span
					aria-hidden="true"
					style={{
						position: 'absolute',
						top: '-6px',
						right: '-6px',
						fontSize: '0.5rem',
						fontWeight: 800,
						letterSpacing: '0.04em',
						textTransform: 'uppercase',
						padding: '0.05rem 0.28rem',
						borderRadius: '999px',
						background: '#e0e7ff',
						color: '#4f46e5',
						lineHeight: 1.3,
						pointerEvents: 'none',
					}}
				>
					Soon
				</span>
			</span>
		</RoleGuard>
	) : null;

	// Preferencias y Solicitudes, a la izquierda de ORCID. Antes eran pestañas del
	// perfil; se movieron acá porque son cosas de la CUENTA, no secciones de la
	// ficha que ve el resto. Por eso mismo las ve además un administrador sobre el
	// perfil ajeno: son justo lo que se necesita mirar para atender a alguien que
	// escribe preguntando en qué quedó su solicitud.
	const etiquetaSolicitudes = isOwnProfile ? 'Mis solicitudes' : 'Solicitudes';
	const seccionesButtons = verSecciones ? (
		<RoleGuard allowedRoles={AUTHENTICATED_ROLES}>
			<button
				type="button"
				className={sidebarStyles.btnSecondary}
				onClick={() => setSeccionLocal('solicitudes')}
			>
				<Inbox size={13} />
				{etiquetaSolicitudes}
			</button>
			<button
				type="button"
				className={sidebarStyles.btnSecondary}
				onClick={() => setSeccionLocal('preferencias')}
			>
				<SlidersHorizontal size={13} />
				Preferencias
			</button>
		</RoleGuard>
	) : null;

	/**
	 * Las mismas acciones, recogidas en un menú para el teléfono.
	 *
	 * Tres botones con texto en la barra superior empujaban el NOMBRE de la persona
	 * fuera de la pantalla: en el perfil propio no se leía de quién era el perfil.
	 * En un menú siguen a un toque y la barra vuelve a decir dónde estás.
	 */
	const menuItems: OverflowMenuItem[] = [
		...(canExport ? [{ label: 'Exportar CV', href: exportHref, Icon: Printer }] : []),
		...(action !== 'none' ? [{ label: actionLabel, href: actionHref, Icon: Pencil }] : []),
		...(isOwnProfile ? [{ label: 'Subir a ORCID', Icon: Upload, disabled: true, soon: true }] : []),
		// Las secciones de la cuenta terminan acá y no en un segundo menú sobre las
		// pestañas: dos menús en la misma pantalla obligaban a recordar cuál
		// guardaba qué. En pantallas anchas siguen siendo botones y este menú no
		// existe.
		...(verSecciones
			? [
					{ label: etiquetaSolicitudes, onSelect: () => setSeccionLocal('solicitudes'), Icon: Inbox },
					{
						label: 'Preferencias',
						onSelect: () => setSeccionLocal('preferencias'),
						Icon: SlidersHorizontal,
					},
				]
			: []),
	];

	const topbarButtons = (
		<>
			{/* Desde `sm` los tres planos: se leen de un vistazo y no cuestan un clic.
			    ORCID abre la fila aunque esté apagado: la barra crece hacia la derecha
			    —"Editar perfil", la acción principal, queda pegada a ese filo— así que
			    lo que todavía no se puede usar va al extremo contrario. */}
			<div className="hidden items-center gap-2 sm:flex">
				{orcidButton}
				{seccionesButtons}
				{exportButton}
				{editButton}
			</div>
			{menuItems.length > 0 ? (
				<div className="sm:hidden">
					<RoleGuard allowedRoles={AUTHENTICATED_ROLES}>
						<OverflowMenu items={menuItems} label="Acciones del perfil" />
					</RoleGuard>
				</div>
			) : null}
		</>
	);

	const isExportOrEditMode = pathname?.endsWith('/exportar') || pathname?.endsWith('/editar');

	if (isExportOrEditMode) {
		return null;
	}

	return (
		<>
			{/* El volver va ANTES que compartir: la barra de la ficha lo empuja contra
			    el filo izquierdo y compartir se queda en el derecho, a la misma altura.
			    Sólo se ve en el teléfono, donde la barra superior no lo lleva. */}
			<BackToDirectoryButton />
			{shareButton}
			{portalTarget && createPortal(topbarButtons, portalTarget)}

			{/* Los modales del dueño se PORTALEAN al body. No es cosmético: esta barra
			    vive dentro de la tarjeta de identidad, que lleva `text-align: center`
			    (IdentityHeader.module.css), y `position: fixed` no corta la herencia
			    de `text-align` — el contenido de los dos modales salía centrado. */}
			{montado && seccion === 'preferencias' && verSecciones
				? createPortal(
						<PreferenciasHubModal
							slug={targetProfile.slug}
							soloLectura={vistaAjenaAdmin}
							nombre={targetProfile.displayName}
							onClose={cerrarSeccion}
						/>,
						document.body,
					)
				: null}
			{montado && seccion === 'solicitudes' && verSecciones
				? createPortal(
						<MisSolicitudesModal
							slug={vistaAjenaAdmin ? targetProfile.slug : undefined}
							nombre={targetProfile.displayName}
							onClose={cerrarSeccion}
						/>,
						document.body,
					)
				: null}
		</>
	);
}
